import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Fade,
  Paper,
  TextField,
} from "@mui/material";
import { Link } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import "@fontsource/poppins";

// See homehero.tsx for the HeroImage and HeroContentBox components. This is no longer used.
export const HeroImage: React.FC = () => {
  return (
    <Box margin={0} padding={0} sx={{ width: "100%" }}>
      <Box
        component="img"
        src="/doctors8.png"
        alt="Doctors"
        sx={{
          width: "100%",
          height: { xs: "200px", sm: "300px" },
          objectFit: "cover",
          display: "block",
        }}
      />
      <Box sx={{ width: "100%", height: "20px", backgroundColor: "#1B2E85" }} />
    </Box>
  );
};

// See homehero.tsx for the HeroImage and HeroContentBox components. This is no longer used.
export const HeroContentBox: React.FC = () => {
  return (
    <Box
      sx={{
        backgroundColor: "#F5F5F5",
        maxWidth: "560px",
        marginTop: "-150px",
        position: "relative",
        zIndex: 2,
        padding: { xs: 3, md: 4 },
        boxShadow: "none",
        mx: "auto",
      }}
    >
      {/* Heading */}
      <Typography
        variant="h4"
        fontWeight="bold"
        textAlign="center"
        color="#1B2E85"
        sx={{ mb: 2 }}
      >
        Artificial Intelligence in <br /> Cancer Genetics
      </Typography>
      <br />

      {/* Centered Divider */}
      <Box
        sx={{
          height: "3px",
          width: "60px",
          backgroundColor: "#1B2E85",
          margin: "0 auto",
          mb: 4,
        }}
      />

      {/* Paragraphs */}
      <Typography
        sx={{ color: "#1B2E85", fontSize: "1.05rem", lineHeight: 1.6, mb: 2 }}
      >
        Genetic testing has become a commodity.
      </Typography>

      <Typography
        sx={{ color: "#1B2E85", fontSize: "1.05rem", lineHeight: 1.6, mb: 2 }}
      >
        The challenge now is managing the carriers in a way that minimizes the
        morbidity and mortality of Hereditary Cancer.
      </Typography>

      <Typography
        sx={{ color: "#1B2E85", fontSize: "1.05rem", lineHeight: 1.6 }}
      >
        Using AI to enhance management strategies in a Hereditary Cancer Clinic
        is the future.
      </Typography>

      {/* Button aligned bottom right */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 4,
        }}
      >
        <Link to="/why-us" style={{ textDecoration: "none" }}>
          <Button
            variant="outlined"
            sx={{
              color: "#1B2E85",
              borderColor: "#1B2E85",
              textTransform: "none",
              fontWeight: "bold",
              px: 3,
              py: 1,
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#1B2E85",
                color: "white",
                borderColor: "#1B2E85",
              },
            }}
          >
            Learn More
          </Button>
        </Link>
      </Box>
    </Box>
  );
};

const cards = [
  {
    src: "/graphbutton.png",
    overlayText: "Genes as Graphs",
    description:
      "See genes and their associated diseases as network using graph database technology.",
    link: "https://graph.hereditarycancer.ai/",
  },
  {
    src: "/cancerclinicbutton2.png",
    overlayText: "Hereditary Cancer Clinic",
    description:
      "Explore how our clinic uses AI to improve the care of patients with hereditary cancer risk.",
    link: "/hereditary-cancer-clinic",
  },
  {
    src: "/ai_in_hereditary_care.png",
    overlayText: "AI in Cancer Genetics",
    description:
      "Some useful examples of how AI is being used in cancer genetics.",
    link: "/cancer-genetics-ai",
  },
  {
    src: "/educationalresourcesbutton.png",
    overlayText: "Educational Resources",
    description:
      "A list of resources to help you understand hereditary cancer and how AI can assist.",
    link: "/resources",
  },
];

export const CardSection: React.FC = () => {
  return (
    <Box
      id="our-solutions"
      sx={{
        width: "100%",
        backgroundColor: "#1B2E85",
        py: { xs: 1, md: 2, xl: 3 },
        px: 0,
        pb: { xs: 1, md: 2, xl: 3 },
      }}
    >
      {/* Section heading and description */}
      <Box sx={{ maxWidth: "800px", mx: "auto", textAlign: "center" }}>
        <Typography
          sx={{
            color: "white",
            fontSize: { xs: "0.75rem", md: "1.1rem", xl: "1.25rem" },
            mb: { xs: 1, md: 2, xl: 3 },
          }}
        >
          AI in Cancer Genetics offers cutting-edge solutions to manage
          hereditary cancer with the help of advanced AI algorithms. This
          website is designed to suggest uses of AI that will minimize the
          impact of hereditary cancer on individuals and their families.
        </Typography>
      </Box>
      {/* Cards */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          flexWrap: "wrap",
          px: { xs: 4, md: 8 },
        }}
      >
        {cards.map((card, index) => (
          <Box
            key={index}
            sx={{
              width: 200,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <HoverCard
              src={card.src}
              label={card.overlayText}
              link={card.link}
              description={card.description}
            />
            <Typography
              variant="subtitle1"
              component="a"
              href={card.link}
              rel="noopener noreferrer"
              sx={{
                mt: 1,
                fontWeight: 500,
                color: "white",
                textDecoration: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  textDecoration: "underline",
                  cursor: "pointer",
                },
              }}
            >
              {card.overlayText}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

type HoverCardProps = {
  src: string;
  alt?: string;
  label?: string;
  description?: string;
  link?: string;
};

const HoverCard = ({
  src,
  alt = "Site preview",
  label = "More Info",
  link = "#",
  description = "No description available.",
}: HoverCardProps) => (
  <Box
    sx={{
      width: 175,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
    }}
  >
    <Box
      component="a"
      href={link}
      rel="noopener noreferrer"
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "2 / 3",
        overflow: "hidden",
        borderRadius: 2,
        border: "2px solid white",
        textDecoration: "none",
        display: "block",
        "&:hover .overlay": {
          opacity: 1,
        },
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
      <Box
        className="overlay"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          opacity: 0,
          transition: "opacity 0.3s ease",
          px: 2,
          boxSizing: "border-box",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 400,
            fontSize: { xs: "0.95rem", sm: "1.05rem" },
            lineHeight: 1.6,
            maxWidth: "100%",
            wordWrap: "break-word",
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const testimonials = [
  {
    header: "Impactful Partnerships",
    quote: "Coming soon.",
    name: "John Doe",
    title: "Partner",
  },
  {
    header: "Patient Experiences",
    quote: "Coming soon.",
    name: "John Doe",
    title: "Hereditary Cancer Survivor",
  },
  {
    header: "Client Stories",
    quote: "Coming soon.",
    name: "John Doe",
    title: "Client",
  },
];

export const TestimonialSlider = () => {
  const [index, setIndex] = useState(0);

  const handlePrev = () => {
    setIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const testimonial = testimonials[index];

  return (
    <Box
      sx={{
        backgroundColor: "#f4f4f4",
        py: { xs: 8, md: 10 },
        px: 4,
        textAlign: "center",
      }}
    >
      <Typography
        variant="h5"
        sx={{ color: "#1B2E85", mb: 4, fontWeight: 600 }}
      >
        {testimonial.header}
      </Typography>

      {/* Fade wrapper */}
      <Fade in timeout={1475} key={index}>
        <Box
          sx={{
            minHeight: { xs: "200px", md: "240px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 400,
              fontSize: { xs: "1.4rem", md: "2rem" },
              maxWidth: "900px",
              mx: "auto",
              color: "#1B2E85",
              mb: 4,
            }}
          >
            “{testimonial.quote}”
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: "firebrick", fontWeight: 500 }}
          >
            {testimonial.name}
          </Typography>
          <Typography variant="body2" sx={{ color: "firebrick" }}>
            {testimonial.title}
          </Typography>
        </Box>
      </Fade>

      {/* Arrows + Clickable Dots */}
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={3}
        mt={6}
      >
        <IconButton onClick={handlePrev}>
          <ArrowBackIosIcon sx={{ color: "#1B2E85" }} />
        </IconButton>

        <Stack direction="row" spacing={1}>
          {testimonials.map((_, i) => (
            <Box
              key={i}
              onClick={() => setIndex(i)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: i === index ? "#1B2E85" : "#ccc",
                cursor: "pointer",
                transition: "background-color 0.3s",
              }}
            />
          ))}
        </Stack>

        <IconButton onClick={handleNext}>
          <ArrowForwardIosIcon sx={{ color: "#1B2E85" }} />
        </IconButton>
      </Stack>
    </Box>
  );
};

export const SectionDivider = () => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "12px",
        backgroundColor: "#1B2E85",
      }}
    />
  );
};

export const GetStartedSection = () => {
  return (
    <Box
      sx={{
        backgroundColor: "white",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        py: { xs: 8, md: 12 },
        px: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          backgroundColor: "#f4f4f4",
          maxWidth: "700px",
          width: "100%",
          px: { xs: 4, md: 6 },
          py: { xs: 6, md: 8 },
          textAlign: "center",
          borderRadius: 0,
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          color="#1B2E85"
          sx={{ fontSize: { xs: "1.8rem", md: "2.4rem" } }}
        >
          Ready to Explore AI-Driven Cancer Genetics?
        </Typography>

        <Box
          sx={{
            width: 60,
            height: 4,
            backgroundColor: "#1B2E85",
            mx: "auto",
            my: 3,
            borderRadius: 2,
          }}
        />

        <Typography
          sx={{
            color: "#1B2E85",
            fontSize: { xs: "0.8rem", md: "1rem" },
            lineHeight: 1.8,
            mb: 5,
          }}
        >
          Discover how AI in Cancer Genetics can revolutionize hereditary cancer
          management. Reach out today to experience the power of AI in genetic
          testing and personalized care.
        </Typography>

        <Button
          variant="outlined"
          onClick={() => {
            const section = document.getElementById("contact-us");
            if (section) {
              section.scrollIntoView({ behavior: "smooth" });
            }
          }}
          sx={{
            color: "#1B2E85",
            borderColor: "#1B2E85",
            fontWeight: "bold",
            px: 4,
            py: 1.5,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#1B2E85",
              color: "white",
            },
          }}
        >
          Get Started
        </Button>
      </Paper>
    </Box>
  );
};

export const ContactUsSection = () => {
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box
      id="contact-us"
      sx={{
        backgroundColor: "#1B2E85",
        color: "white",
        py: { xs: 6, md: 8 },
        px: 3,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ maxWidth: "700px", width: "100%" }}>
        {/* Header */}
        <Typography
          variant="h4"
          align="center"
          fontWeight="bold"
          mb={6}
          sx={{ fontSize: { xs: "2rem", md: "2.5rem" } }}
        >
          Contact Us
        </Typography>

        {/* Form */}
        <form noValidate>
          <Stack spacing={4}>
            {/* First + Last Name side-by-side on desktop */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={4}>
              <TextField
                label="First Name"
                fullWidth
                required
                variant="standard"
                InputLabelProps={{ style: { color: "white" } }}
                InputProps={{
                  style: { color: "white" },
                  disableUnderline: false,
                  sx: {
                    "&:before": { borderBottom: "1px solid white" },
                    "&:hover:not(.Mui-disabled):before": {
                      borderBottom: "2px solid white",
                    },
                    "&:after": { borderBottom: "2px solid white" },
                  },
                }}
              />
              <TextField
                label="Last Name"
                fullWidth
                required
                variant="standard"
                InputLabelProps={{ style: { color: "white" } }}
                InputProps={{
                  style: { color: "white" },
                  disableUnderline: false,
                  sx: {
                    "&:before": { borderBottom: "1px solid white" },
                    "&:hover:not(.Mui-disabled):before": {
                      borderBottom: "2px solid white",
                    },
                    "&:after": { borderBottom: "2px solid white" },
                  },
                }}
              />
            </Stack>

            <TextField
              label="Email"
              fullWidth
              required
              variant="standard"
              type="email"
              InputLabelProps={{ style: { color: "white" } }}
              InputProps={{
                style: { color: "white" },
                disableUnderline: false,
                sx: {
                  "&:before": { borderBottom: "1px solid white" },
                  "&:hover:not(.Mui-disabled):before": {
                    borderBottom: "2px solid white",
                  },
                  "&:after": { borderBottom: "2px solid white" },
                },
              }}
            />

            <TextField
              label="Message"
              fullWidth
              multiline
              rows={4}
              variant="standard"
              InputLabelProps={{ style: { color: "white" } }}
              InputProps={{
                style: { color: "white" },
                disableUnderline: false,
                sx: {
                  "&:before": { borderBottom: "1px solid white" },
                  "&:hover:not(.Mui-disabled):before": {
                    borderBottom: "2px solid white",
                  },
                  "&:after": { borderBottom: "2px solid white" },
                },
              }}
            />

            {/* Submit Button */}
            <Box textAlign="center" mt={2}>
              <Button
                variant="outlined"
                sx={{
                  color: "white",
                  borderColor: "white",
                  px: 5,
                  py: 1.5,
                  fontWeight: "bold",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "white",
                    color: "#1B2E85",
                  },
                }}
              >
                Submit
              </Button>
            </Box>
          </Stack>
        </form>
        {/* Footer Info Section */}
        <Box
          mt={8}
          sx={{
            borderTop: "1px solid rgba(255, 255, 255, 0.3)",
            pt: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            flexWrap="wrap"
            color="white"
            fontSize="1rem"
          >
            <Typography>
              Address. 86 Jonathan Lucas St, Charleston, SC 29425
            </Typography>
            <Typography>Tel. PHONEGOESHERE</Typography>
            <Typography>Email. EMAILGOESHERE</Typography>
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={3}
          >
            <Typography fontSize="0.9rem">© COPYRIGHT</Typography>

            <IconButton
              onClick={handleScrollToTop}
              sx={{
                color: "white",
                border: "none",
                padding: 1,
                "&:hover": { transform: "scale(1.1)" },
              }}
            >
              <KeyboardArrowUpIcon fontSize="large" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
